import { Fragment } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { TrashIcon, PlusIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import InputBase from '../core/form/InputBase';
import ModuleBaseForm from './ModuleBaseForm';
import SearchableSelect from '../core/form/SearchableSelect';
import { stringify } from '@/utils/miscUtils';

const EMISSION_SPECIES = ['voc', 'co', 'co2', 'co2_equivalent', 'ch4', 'sox', 'nox'];

const MEBalanceInputs = ({ typeOptions, typeToUnitsMap, defaultValues, warnings }) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    control,
    getValues,
    trigger,
  } = useFormContext();

  const inputsArray = useFieldArray({
    control,
    name: 'inputs',
  });

  const outputsArray = useFieldArray({
    control,
    name: 'outputs',
  });

  const inputs = watch('inputs');
  const outputs = watch('outputs');
  const modelType = watch('model_type');

  const handleCopyInput = index => {
    const valuesToCopy = inputs[index];

    inputsArray.append({
      label: `${valuesToCopy.label} (Copy)`,
      name: `output_${inputsArray.fields.length + 1}`,
      species: valuesToCopy.species,
      value: valuesToCopy.value,
      unit: valuesToCopy.unit,
    });
  };

  const handleCopyOutput = index => {
    const valuesToCopy = outputs[index];

    outputsArray.append({
      label: `${valuesToCopy.label} (Copy)`,
      name: `output_${outputsArray.fields.length + 1}`,
      species: valuesToCopy.species,
      value: valuesToCopy.value,
      unit: valuesToCopy.unit,
      allocatable: valuesToCopy.allocatable,
      emissions: valuesToCopy.emissions,
    });
  };

  const handleAddInput = () => {
    const species = defaultValues.inputs[0].species;
    const defaultUnit = typeToUnitsMap[species]?.[0];

    inputsArray.append({
      label: `Option ${inputsArray.fields.length + 1}`,
      name: `input_${inputsArray.fields.length + 1}`,
      species: species,
      value: '',
      unit: stringify(defaultUnit),
    });
  };

  const handleAddOutput = () => {
    const species = defaultValues.outputs[0].species;
    const defaultUnit = typeToUnitsMap[species]?.[0];

    outputsArray.append({
      label: `Option ${outputsArray.fields.length + 1}`,
      name: `output_${outputsArray.fields.length + 1}`,
      species: species,
      value: '',
      unit: stringify(defaultUnit),
      allocatable: true,
      emissions: false,
    });
  };

  const handleAllocateEmissionsChange = (index, value) => {
    if (value) {
      const outputsCopy = [...outputs];
      outputsCopy[index].emissions = false;
    }
  };

  const handleEmissionChange = (index, value) => {
    if (value) {
      const outputsCopy = [...outputs];
      outputsCopy[index].allocatable = false;
      setValue(`outputs.${index}.allocatable`, false);
    }
  };

  const handleOutputTypeChange = (index, value) => {
    if (EMISSION_SPECIES.includes(value)) {
      setValue(`outputs.${index}.emissions`, true);
      setValue(`outputs.${index}.allocatable`, false);
    } else {
      setValue(`outputs.${index}.emissions`, false);
      setValue(`outputs.${index}.allocatable`, true);
    }
  };

  const handleRemoveInput = index => {
    inputsArray.remove(index);
  };

  const handleRemoveOutput = index => {
    outputsArray.remove(index);
  };

  const handleInputSpeciesChange = (index, value) => {
    const defaultUnit = typeToUnitsMap[value]?.[0];
    if (defaultUnit) {
      setValue(`inputs.${index}.unit`, stringify(defaultUnit));
    }
  };

  const handleOutputSpeciesChange = (index, value) => {
    const defaultUnit = typeToUnitsMap[value]?.[0];
    if (defaultUnit) {
      setValue(`outputs.${index}.unit`, stringify(defaultUnit));
    }
    handleOutputTypeChange(index, value);
  };

  // Factory function to create validation for at least one value > 0
  const createValidateAtLeastOne = (fieldName, errorMessage) => {
    return () => {
      const allItems = getValues(fieldName) || [];
      const hasItemWithValue = allItems.some(item => {
        const value = parseFloat(item?.value);
        return !isNaN(value) && value > 0;
      });
      return hasItemWithValue || errorMessage;
    };
  };

  const validateAtLeastOneInput = createValidateAtLeastOne(
    'inputs',
    'At least one input must have a value greater than 0',
  );

  const validateAtLeastOneOutput = createValidateAtLeastOne(
    'outputs',
    'At least one output must have a value greater than 0',
  );

  // Factory function to create value change handler
  const createValueChangeHandler = fieldName => {
    return async () => {
      const allItems = getValues(fieldName) || [];
      const valueFields = allItems.map((_, idx) => `${fieldName}.${idx}.value`);
      await trigger(valueFields);
    };
  };

  const handleInputValueChange = createValueChangeHandler('inputs');
  const handleOutputValueChange = createValueChangeHandler('outputs');

  // Factory function to create value registration props
  const createValueRegistration = (fieldName, validateFn, changeHandler) => {
    return index => {
      const { onChange, ...rest } = register(`${fieldName}.${index}.value`, {
        pattern: {
          value: /^-?\d*\.?\d+$/,
          message: 'Please enter a number',
        },
        required: 'Default value is required',
        validate: validateFn,
      });
      return {
        ...rest,
        onChange: async e => {
          onChange(e);
          await changeHandler();
        },
      };
    };
  };

  const getInputValueRegistration = createValueRegistration('inputs', validateAtLeastOneInput, handleInputValueChange);
  const getOutputValueRegistration = createValueRegistration(
    'outputs',
    validateAtLeastOneOutput,
    handleOutputValueChange,
  );

  return (
    <div className="space-y-6">
      <p className="text-md font-medium text-center mb-6 text-gray-400">Part 1: Mass and Energy Balance</p>
      <ModuleBaseForm />

      {Array.isArray(warnings) && warnings.length > 0 && (
        <div className="alert alert-warning bg-yellow-50 border-yellow-400 text-yellow-800 p-4 mb-4">
          <ul className="list-disc list-inside text-sm pl-4">
            {warnings.map((warning, idx) => (
              <li key={idx}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-8">
        <h4 className="font-medium mb-2 border-b border-gray-200 pb-2">Inputs</h4>
        <div className="overflow-x-auto">
          <table className="table table-sm w-full [&_th]:pl-0 [&_td]:pl-0">
            <thead className="truncate text-xs font-semibold">
              <tr>
                <th>Label</th>
                <th>Type</th>
                <th>Default Value</th>
                <th>Unit</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {inputsArray.fields.map((field, index) => (
                <Fragment key={`input-${field.id || index}`}>
                  <tr>
                    <td>
                      <InputBase
                        hasError={!!errors.inputs?.[index]?.label}
                        {...register(`inputs.${index}.label`, { required: 'Label is required' })}
                      />
                    </td>
                    <td>
                      <SearchableSelect
                        name={`inputs.${index}.species`}
                        options={typeOptions}
                        className={`select-sm w-full ${errors.inputs?.[index]?.species ? 'select-error' : ''}`}
                        registerOptions={{
                          required: 'Type is required',
                          onChange: e => handleInputSpeciesChange(index, e.target.value),
                        }}
                        maxVisibleItems={8}
                      />
                    </td>
                    <td>
                      <InputBase
                        hasError={!!errors.inputs?.[index]?.value}
                        placeholder="Default value"
                        {...getInputValueRegistration(index)}
                      />
                    </td>
                    <td>
                      <select
                        className={`select select-bordered select-sm w-full ${errors.inputs?.[index]?.unit ? 'select-error' : ''}`}
                        {...register(`inputs.${index}.unit`, { required: 'Unit is required' })}
                      >
                        {typeToUnitsMap?.[inputs?.[index]?.species]?.map(option => (
                          <option key={option.label} value={stringify(option)}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <div className="flex space-x-2">
                        <button type="button" onClick={() => handleCopyInput(index)} className="btn btn-ghost btn-sm">
                          <DocumentDuplicateIcon className="h-5 w-5 text-gray-500" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveInput(index)}
                          className="btn btn-ghost btn-sm"
                          disabled={inputsArray.fields.length === 1}
                        >
                          <TrashIcon className="h-5 w-5 text-gray-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={7}>
                      <div className="text-error text-xs text-center">{errors?.inputs?.[index]?.value?.message}</div>
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {modelType !== 'aspen' && (
          <div className="flex justify-center mb-2">
            <button type="button" onClick={handleAddInput} className="btn btn-ghost btn-sm gap-2">
              <PlusIcon className="h-4 w-4" />
              Add Stream
            </button>
          </div>
        )}
      </div>
      <hr />
      <div>
        <h4 className="mt-8 font-medium mb-2 border-b border-gray-200 pb-2">Outputs</h4>
        <div className="overflow-x-auto">
          <table className="table table-xs w-full">
            <thead>
              <tr>
                <th>Label</th>
                <th>Type</th>
                <th>Default Value</th>
                <th>Unit</th>
                <th>Allocate emissions</th>
                <th>Emission</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {outputsArray.fields.map((field, index) => (
                <Fragment key={`output-${field.id || index}`}>
                  <tr>
                    <td>
                      <InputBase {...register(`outputs.${index}.label`, { required: 'Label is required' })} />
                    </td>
                    <td>
                      <SearchableSelect
                        name={`outputs.${index}.species`}
                        options={typeOptions}
                        className={`select-sm w-full ${errors.outputs?.[index]?.species ? 'select-error' : ''}`}
                        registerOptions={{
                          required: 'Type is required',
                          onChange: e => handleOutputSpeciesChange(index, e.target.value),
                        }}
                        maxVisibleItems={8}
                      />
                    </td>
                    <td>
                      <InputBase hasError={!!errors.outputs?.[index]?.value} {...getOutputValueRegistration(index)} />
                    </td>
                    <td>
                      <select
                        className={`select select-bordered select-sm w-full ${errors.outputs?.[index]?.unit ? 'select-error' : ''}`}
                        {...register(`outputs.${index}.unit`, { required: 'Unit is required' })}
                      >
                        {typeToUnitsMap?.[outputs?.[index]?.species]?.map(option => (
                          <option key={option.label} value={stringify(option)}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        {...register(`outputs.${index}.allocatable`, {
                          onChange: e => handleAllocateEmissionsChange(index, e.target.checked),
                        })}
                        disabled={outputs?.[index]?.emissions}
                      />
                    </td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        {...register(`outputs.${index}.emissions`, {
                          onChange: e => handleEmissionChange(index, e.target.checked),
                        })}
                        disabled={outputs?.[index]?.allocatable}
                      />
                    </td>
                    <td>
                      <div className="flex space-x-2">
                        <button type="button" onClick={() => handleCopyOutput(index)} className="btn btn-ghost btn-sm">
                          <DocumentDuplicateIcon className="h-5 w-5 text-gray-500" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveOutput(index)}
                          className="btn btn-ghost btn-sm"
                          disabled={outputsArray.fields.length === 1}
                        >
                          <TrashIcon className="h-5 w-5 text-gray-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={7}>
                      <div className="text-error text-xs text-center">{errors?.outputs?.[index]?.value?.message}</div>
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {modelType !== 'aspen' && (
          <div className="flex justify-center mb-2">
            <button type="button" onClick={handleAddOutput} className="btn btn-ghost btn-sm gap-2">
              <PlusIcon className="h-4 w-4" />
              Add Stream
            </button>
          </div>
        )}
      </div>
      <hr />
    </div>
  );
};

MEBalanceInputs.propTypes = {
  typeOptions: PropTypes.array.isRequired,
  typeToUnitsMap: PropTypes.object.isRequired,
  defaultValues: PropTypes.object.isRequired,
  warnings: PropTypes.array,
};

export default MEBalanceInputs;
